import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideZonelessChangeDetection, importProvidersFrom } from '@angular/core';

// NGXS
import { provideStore } from '@ngxs/store';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';

import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { environment } from './environments/environment';

// States
import {
    AuthState,
    CompanyState,
    ContactState,
    ContractState,
    ContractTypeState,
    ItemState,
    EquipmentTypeState,
    EquipmentCategoryState,
    InstallationState,
    ServiceState,
    IssueState,
    IssueTypeState,
    DashboardState,
    UploadState,
    CheckListState,
    ProfileState,
    LocationState,
} from './app/data/state';

// Interceptor
import { baseHttpInterceptor } from './app/core/interceptor/base-http.interceptor';

// Utils
import { isTokenExpired } from './app/shared/util';

// Guards
import { AuthGuard } from './app/core/guard/auth.guard';
import { RoleGuard } from './app/core/guard/role.guard';

// ngx-bootstrap
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';

// Password strength
import { PasswordStrengthMeterModule } from 'angular-password-strength-meter';
import { PasswordStrengthService } from './app/core/service/password-strength.service';

/**
 * It returns the base URL of the API
 * @returns The base url for the api.
 */
export function getBaseUrl() {
    return `${environment.apiBaseUrl}/${environment.apiPrefix}`;
}

bootstrapApplication(AppComponent, {
    providers: [
        // Zoneless change detection
        provideZonelessChangeDetection(),

        // Animations
        provideAnimations(),

        // Base URL
        { provide: 'BASE_URL', useFactory: getBaseUrl, deps: [] },

        // HTTP Client with Interceptor
        provideHttpClient(withInterceptors([baseHttpInterceptor])),

        // Routing
        provideRouter(routes, withComponentInputBinding()),

        // Guards
        AuthGuard,
        RoleGuard,

        // NGXS Store
        provideStore(
            [
                AuthState,
                CompanyState,
                ContactState,
                ContractState,
                ContractTypeState,
                ItemState,
                EquipmentTypeState,
                EquipmentCategoryState,
                InstallationState,
                ServiceState,
                IssueState,
                IssueTypeState,
                DashboardState,
                UploadState,
                CheckListState,
                ProfileState,
                LocationState,
            ],
            { developmentMode: !environment.production },
            withNgxsStoragePlugin({
                keys: [AuthState],
                afterDeserialize: (obj, key) => {
                    if (key === 'auth' && obj?.token && isTokenExpired(obj?.token)) {
                        return obj;
                    }
                    return obj;
                },
            }),
            withNgxsLoggerPlugin({ disabled: environment.production }),
            withNgxsReduxDevtoolsPlugin({ disabled: environment.production })
        ),

        // ngx-bootstrap providers
        importProvidersFrom(
            BsDropdownModule.forRoot(),
            AccordionModule.forRoot(),
            ModalModule.forRoot(),
            ButtonsModule.forRoot(),
            TabsModule.forRoot(),
            TooltipModule.forRoot(),
            ProgressbarModule.forRoot(),
            PopoverModule.forRoot(),
            BsDatepickerModule.forRoot(),
            PasswordStrengthMeterModule.forRoot({ serviceClass: PasswordStrengthService })
        ),
    ],
}).catch((err) => console.error(err));
